/**
 * <size-guide-modal> — beden kılavuzu Web Component
 * Mevcut tema'dan migrate edilen logic (manken canvas analizi + SVG silüet overlay).
 *
 * Sorumluluklar:
 *  - Native <dialog> open/close
 *  - Sürüklenebilir başlık (desktop)
 *  - Chart data + composition (kumaş esnekliği) JSON parsing
 *  - Manken 3 JPEG piksel analizi (vücut kenarları + basen kontur)
 *  - SVG overlay çizimi (etek / blazer şeması)
 *  - Beden hesabı (ölçü + esnek kumaş toleransı)
 *  - Önerilen beden tabloda highlight
 */
class SizeGuideModal extends HTMLElement {
  constructor() {
    super();
    this.chartKey = this.dataset.chartKey;
    this.dialog = this.querySelector('[data-sg-dialog]');
    this.form = this.querySelector('[data-sg-form]');
    this.result = this.querySelector('[data-sg-result]');
    this.recSize = this.querySelector('[data-sg-rec-size]');
    this.recNote = this.querySelector('[data-sg-rec-note]');
    this.avatar = this.querySelector('.sg__avatar-svg');
    this.overlay = this.querySelector('.sg__product-overlay');
    this.boyInput = this.form?.querySelector('input[name="user_boy"]');
    this.boyStat = this.querySelector('[data-sg-boy]');
    this.lengthLabel = this.querySelector('[data-sg-length-label]');
    this.lengthInfo = this.querySelector('[data-sg-length-info]');
    this.note = this.querySelector('[data-sg-note]');

    // Data parsing
    try {
      this.chart = JSON.parse(this.querySelector('[data-sg-chart-data]')?.textContent || '{}');
    } catch { this.chart = {}; }
    try {
      this.composition = JSON.parse(this.querySelector('[data-sg-composition]')?.textContent || '""') || '';
    } catch { this.composition = ''; }

    this.primaryFields = (this.chart.fields || []).filter((f) => f.primary);
    this.bodyEdges = null;
    this.hipWidestEdge = null;

    // Kumaş esnek mi?
    const stretchRegex = /likra|lycra|elastan|spandex|elastik|elasti̇k|stretch/i;
    this.isStretchy = stretchRegex.test(this.composition || '');

    // Event listeners
    this.querySelector('[data-sg-open]')?.addEventListener('click', () => this.open());
    this.querySelector('[data-sg-close]')?.addEventListener('click', () => this.close());
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.calculate();
    });
    this.boyInput?.addEventListener('input', () => this.updateAvatar());
    this.form?.querySelectorAll('input').forEach((inp) => {
      inp.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this.calculate(); }
      });
    });

    // Draggable head (desktop only)
    this.setupDrag();

    // İlk avatar çizimi
    this.updateAvatar();
    this.loadBodyEdges().then(() => this.updateAvatar());
  }

  open() {
    this.dialog?.showModal();
  }

  close() {
    this.dialog?.close();
  }

  setupDrag() {
    const handle = this.querySelector('[data-sg-drag]');
    if (!handle || !this.dialog) return;
    let dragging = false, startX = 0, startY = 0, originX = 0, originY = 0;

    handle.addEventListener('mousedown', (e) => {
      if (e.target.closest('[data-sg-close]')) return;
      if (!window.matchMedia('(min-width: 768px)').matches) return; // mobile no drag
      dragging = true;
      startX = e.clientX; startY = e.clientY;
      const rect = this.dialog.getBoundingClientRect();
      originX = rect.left; originY = rect.top;
      this.dialog.style.transform = 'none';
      this.dialog.style.left = originX + 'px';
      this.dialog.style.top = originY + 'px';
      e.preventDefault();
    });
    document.addEventListener('mousemove', (e) => {
      if (!dragging) return;
      const nL = originX + (e.clientX - startX);
      const nT = originY + (e.clientY - startY);
      this.dialog.style.left = Math.max(-this.dialog.offsetWidth + 80, Math.min(nL, window.innerWidth - 40)) + 'px';
      this.dialog.style.top = Math.max(0, Math.min(nT, window.innerHeight - 60)) + 'px';
    });
    document.addEventListener('mouseup', () => { dragging = false; });
  }

  /* ===== Manken piksel analizi ===== */

  analyzeMannequin(url, ySvgStart, ySvgEnd) {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.width; c.height = img.height;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0);
        let data;
        try { data = ctx.getImageData(0, 0, img.width, img.height).data; }
        catch { resolve([]); return; }
        const W = img.width, H = img.height, cxImg = Math.floor(W / 2);
        const isDark = (px, py) => {
          const i = (py * W + px) * 4;
          return data[i] + data[i + 1] + data[i + 2] < 600;
        };
        const edges = [];
        for (let py = 0; py < H; py++) {
          let rightPx = -1, leftPx = -1, armRightPx = -1, armLeftPx = -1;
          for (let px = cxImg; px < W; px++) if (isDark(px, py)) { rightPx = px; break; }
          for (let px = cxImg; px >= 0; px--) if (isDark(px, py)) { leftPx = px; break; }
          for (let px = W - 1; px > cxImg; px--) if (isDark(px, py)) { armRightPx = px; break; }
          for (let px = 0; px < cxImg; px++) if (isDark(px, py)) { armLeftPx = px; break; }
          if (leftPx >= 0 && rightPx > leftPx) {
            const ySvg = ySvgStart + (py / H) * (ySvgEnd - ySvgStart);
            edges.push({
              y: ySvg,
              left: 60 + (leftPx / W) * 357,
              right: 60 + (rightPx / W) * 357,
              armLeft: armLeftPx >= 0 ? 60 + (armLeftPx / W) * 357 : null,
              armRight: armRightPx >= 0 ? 60 + (armRightPx / W) * 357 : null,
            });
          }
        }
        resolve(edges);
      };
      img.onerror = () => resolve([]);
      img.src = url;
    });
  }

  async loadBodyEdges() {
    if (this.bodyEdges) return this.bodyEdges;
    const assetUrl = (name) => {
      // image src'leri SVG'den çıkar
      const imgs = this.avatar?.querySelectorAll('image') || [];
      for (const i of imgs) {
        const href = i.getAttribute('href') || '';
        if (href.includes(name)) return href;
      }
      return '/assets/' + name;
    };
    const [e1, e2, e3] = await Promise.all([
      this.analyzeMannequin(assetUrl('mannequinImgPart1.jpeg'), 22, 491),
      this.analyzeMannequin(assetUrl('mannequinImgPart2.jpeg'), 491, 631),
      this.analyzeMannequin(assetUrl('mannequinImgPart3.jpeg'), 631, 1177),
    ]);
    this.bodyEdges = [...e1, ...e2, ...e3];

    // Basenin en geniş noktası
    let widestW = 0, widest = null;
    for (const e of this.bodyEdges) {
      if (e.y < 491 || e.y > 720) continue;
      const w = e.right - e.left;
      if (w > widestW) { widestW = w; widest = e; }
    }
    this.hipWidestEdge = widest;

    // Omuz tespit
    const upperBody = e1.filter((e) => e.y >= 100 && e.y <= 380);
    let maxUpperW = 0;
    for (const e of upperBody) {
      const w = e.right - e.left;
      if (w > maxUpperW) maxUpperW = w;
    }
    const shoulderThreshold = maxUpperW * 0.6;
    let shoulderY = 230;
    for (const e of upperBody) {
      if ((e.right - e.left) >= shoulderThreshold) {
        shoulderY = Math.round(e.y);
        break;
      }
    }
    this.avatar.dataset.yShoulder = String(shoulderY);
    return this.bodyEdges;
  }

  /* ===== Avatar overlay çizimi ===== */

  widthFromCircumference(cm) { return cm * 1.1; }

  updateAvatar() {
    if (!this.avatar || !this.chart.rows) return;
    const userBoy = parseInt(this.boyInput?.value || this.avatar.dataset.defaultBoyCm, 10) || 168;
    if (this.boyStat) this.boyStat.textContent = userBoy + ' cm';

    const recBeden = this.recSize?.textContent || '';
    const recRow = this.chart.rows.find((r) => r.beden === recBeden) || this.chart.rows[2] || this.chart.rows[0];
    const belCm = recRow.bel || 76;
    const basenCm = recRow.basen || 100;

    const ds = this.avatar.dataset;
    const yWaist = parseFloat(ds.yWaist);
    const yFloor = parseFloat(ds.yFloor);
    const yShoulder = parseFloat(ds.yShoulder);
    const yBust = parseFloat(ds.yBust);
    const yHip = parseFloat(ds.yHip);
    const yCrotch = parseFloat(ds.yCrotch);
    const yKnee = parseFloat(ds.yKnee);
    const yAnkle = parseFloat(ds.yAnkle);

    const anchor = this.chart.anchor || { y_start: 'waist', y_end: 'knee', anatomy_ratio: 0.37 };
    const yStart = parseFloat(ds['y' + anchor.y_start.charAt(0).toUpperCase() + anchor.y_start.slice(1)]);
    const yEnd = parseFloat(ds['y' + anchor.y_end.charAt(0).toUpperCase() + anchor.y_end.slice(1)]);
    const pxPerCm = (yEnd - yStart) / (anchor.anatomy_ratio * userBoy);

    const waistW = this.widthFromCircumference(belCm);
    const hipW = this.widthFromCircumference(basenCm);

    let cx = 240;
    if (this.bodyEdges?.length > 0) {
      const samples = this.bodyEdges.filter((e) => e.y >= 491 && e.y <= 700).map((e) => (e.left + e.right) / 2);
      if (samples.length) cx = samples.reduce((a, b) => a + b, 0) / samples.length;
    }

    const overlayCfg = this.chart.overlay || { shape: 'skirt', length_field: 'boy' };
    const lengthVals = this.chart.rows.map((r) => r[overlayCfg.length_field]).filter((v) => typeof v === 'number');
    const productLengthCm = lengthVals.length ? Math.round(lengthVals.reduce((a, b) => a + b, 0) / lengthVals.length) : 60;
    const overlayHeight = productLengthCm * pxPerCm;
    const overlayTop = overlayCfg.shape === 'blazer' ? yShoulder : yWaist;
    const yTop = overlayTop, yBot = overlayTop + overlayHeight;
    const margin = 4;
    const ns = 'http://www.w3.org/2000/svg';

    this.overlay.innerHTML = '';

    if (overlayCfg.shape === 'blazer') {
      this.drawBlazer({ ns, cx, yTop, yBot, overlayHeight, pxPerCm, yShoulder, yBust, yWaist, yHip, productLengthCm });
    } else {
      this.drawSkirt({ ns, cx, yTop, yBot, waistW, hipW, margin, productLengthCm, yWaist, yKnee });
    }
  }

  drawSkirt({ ns, cx, yTop, yBot, waistW, hipW, margin, productLengthCm, yWaist, yKnee }) {
    let leftPoints = [], rightPoints = [];
    if (this.bodyEdges?.length > 0 && this.hipWidestEdge) {
      const yHipWidest = this.hipWidestEdge.y;
      const hipHalfW = (this.hipWidestEdge.right - this.hipWidestEdge.left) / 2 + margin;
      for (const e of this.bodyEdges) {
        if (e.y < yTop) continue;
        if (e.y > yBot) break;
        if (e.y <= yHipWidest) {
          leftPoints.push({ x: e.left - margin, y: e.y });
          rightPoints.push({ x: e.right + margin, y: e.y });
        } else {
          const cxBody = (this.hipWidestEdge.left + this.hipWidestEdge.right) / 2;
          leftPoints.push({ x: cxBody - hipHalfW, y: e.y });
          rightPoints.push({ x: cxBody + hipHalfW, y: e.y });
        }
      }
      if (leftPoints.length > 0) {
        const lastL = leftPoints[leftPoints.length - 1];
        const lastR = rightPoints[rightPoints.length - 1];
        if (lastL.y < yBot) {
          leftPoints.push({ x: lastL.x, y: yBot });
          rightPoints.push({ x: lastR.x, y: yBot });
        }
      }
    } else {
      const halfTop = Math.max(waistW / 2 + 8, 75);
      const halfBot = hipW / 2 * 1.4 + 20;
      leftPoints = [{ x: cx - halfTop, y: yTop }, { x: cx - halfBot, y: yBot }];
      rightPoints = [{ x: cx + halfTop, y: yTop }, { x: cx + halfBot, y: yBot }];
    }
    if (leftPoints.length === 0) return;

    let d = `M ${leftPoints[0].x.toFixed(1)} ${leftPoints[0].y.toFixed(1)}`;
    for (let i = 1; i < leftPoints.length; i++) d += ` L ${leftPoints[i].x.toFixed(1)} ${leftPoints[i].y.toFixed(1)}`;
    for (let i = rightPoints.length - 1; i >= 0; i--) d += ` L ${rightPoints[i].x.toFixed(1)} ${rightPoints[i].y.toFixed(1)}`;
    d += ' Z';

    const skirt = document.createElementNS(ns, 'path');
    skirt.setAttribute('d', d);
    skirt.setAttribute('fill', '#c9a37a');
    skirt.setAttribute('fill-opacity', '1');
    skirt.setAttribute('stroke', '#8a6a3f');
    skirt.setAttribute('stroke-width', '1.4');
    skirt.setAttribute('stroke-linejoin', 'round');
    this.overlay.appendChild(skirt);

    const wbL = leftPoints[0].x, wbR = rightPoints[0].x;
    const band = document.createElementNS(ns, 'rect');
    band.setAttribute('x', wbL);
    band.setAttribute('y', yTop - 1);
    band.setAttribute('width', wbR - wbL);
    band.setAttribute('height', 6);
    band.setAttribute('fill', '#8a6a3f');
    this.overlay.appendChild(band);

    if (this.lengthLabel) this.lengthLabel.textContent = 'Etek belden';
    if (this.lengthInfo) this.lengthInfo.textContent = productLengthCm + ' cm';
    if (this.note) {
      const skirtEnd = yWaist + (yBot - yTop);
      if (skirtEnd < yKnee - 30) this.note.textContent = 'Bu etek senin boyunda diz üstü kalır.';
      else if (skirtEnd <= yKnee + 30) this.note.textContent = 'Bu etek diz hizası civarında.';
      else this.note.textContent = 'Bu etek diz altı / midi olarak durur.';
    }
  }

  drawBlazer({ ns, cx, yShoulder, yBust, yWaist, yHip, overlayHeight, pxPerCm, productLengthCm }) {
    const HPS_OFFSET = 30;
    const yHPS = Math.max(yShoulder - HPS_OFFSET, 100);
    const yShoulderEdge = yShoulder;
    const halfShoulder = 92, halfBust = 86, halfWaist = 70;
    const halfHip = this.hipWidestEdge ? (this.hipWidestEdge.right - this.hipWidestEdge.left) / 2 + 6 : 94;
    const yBotEff = yHPS + overlayHeight;
    const halfHem = yBotEff > yHip ? halfHip : halfHip;
    const vTop = yHPS + 4;
    const vBot = vTop + 25 * pxPerCm;
    const vHalfW = 28;

    const d = [
      `M ${cx - halfShoulder} ${yShoulderEdge}`,
      `L ${cx - vHalfW} ${vTop}`,
      `L ${cx} ${vBot}`,
      `L ${cx + vHalfW} ${vTop}`,
      `L ${cx + halfShoulder} ${yShoulderEdge}`,
      `Q ${cx + halfShoulder + 2} ${(yShoulderEdge + yBust) / 2} ${cx + halfBust} ${yBust}`,
      `L ${cx + halfWaist} ${yWaist}`,
      `L ${cx + halfHip} ${yHip}`,
      `L ${cx + halfHem} ${yBotEff}`,
      `L ${cx + 14} ${yBotEff}`,
      `L ${cx} ${yBotEff - 25 * pxPerCm}`,
      `L ${cx - 14} ${yBotEff}`,
      `L ${cx - halfHem} ${yBotEff}`,
      `L ${cx - halfHip} ${yHip}`,
      `L ${cx - halfWaist} ${yWaist}`,
      `L ${cx - halfBust} ${yBust}`,
      `Q ${cx - halfShoulder - 2} ${(yShoulderEdge + yBust) / 2} ${cx - halfShoulder} ${yShoulderEdge}`,
      'Z',
    ].join(' ');

    const blazer = document.createElementNS(ns, 'path');
    blazer.setAttribute('d', d);
    blazer.setAttribute('fill', '#4a5e8a');
    blazer.setAttribute('fill-opacity', '0.92');
    blazer.setAttribute('stroke', '#2c3a55');
    blazer.setAttribute('stroke-width', '1.2');
    blazer.setAttribute('stroke-linejoin', 'round');
    this.overlay.appendChild(blazer);

    // Kollar
    const kolVals = this.chart.rows.map((r) => r.kol).filter((v) => typeof v === 'number');
    const kolLengthCm = kolVals.length ? kolVals.reduce((a, b) => a + b, 0) / kolVals.length : 60;
    const sleeveLen = kolLengthCm * pxPerCm;
    const sleeveBotY = yShoulderEdge + sleeveLen;
    const sleeveTopW = 22, sleeveBotW = 16;
    const lOuterTop = cx - halfShoulder + 2, lOuterBot = cx - halfShoulder - 6;
    const lSleeve = document.createElementNS(ns, 'path');
    lSleeve.setAttribute('d', `M ${lOuterTop} ${yShoulderEdge} L ${lOuterTop - sleeveTopW} ${yShoulderEdge} L ${lOuterBot - sleeveBotW} ${sleeveBotY} L ${lOuterBot} ${sleeveBotY} Z`);
    lSleeve.setAttribute('fill', '#4a5e8a');
    lSleeve.setAttribute('fill-opacity', '0.92');
    lSleeve.setAttribute('stroke', '#2c3a55');
    lSleeve.setAttribute('stroke-width', '1.2');
    this.overlay.appendChild(lSleeve);

    const rOuterTop = cx + halfShoulder - 2, rOuterBot = cx + halfShoulder + 6;
    const rSleeve = document.createElementNS(ns, 'path');
    rSleeve.setAttribute('d', `M ${rOuterTop} ${yShoulderEdge} L ${rOuterTop + sleeveTopW} ${yShoulderEdge} L ${rOuterBot + sleeveBotW} ${sleeveBotY} L ${rOuterBot} ${sleeveBotY} Z`);
    rSleeve.setAttribute('fill', '#4a5e8a');
    rSleeve.setAttribute('fill-opacity', '0.92');
    rSleeve.setAttribute('stroke', '#2c3a55');
    rSleeve.setAttribute('stroke-width', '1.2');
    this.overlay.appendChild(rSleeve);

    const button = document.createElementNS(ns, 'circle');
    button.setAttribute('cx', cx);
    button.setAttribute('cy', vBot + 30);
    button.setAttribute('r', 4);
    button.setAttribute('fill', '#1a2233');
    this.overlay.appendChild(button);

    if (this.lengthLabel) this.lengthLabel.textContent = 'Ceket ön boy';
    if (this.lengthInfo) this.lengthInfo.textContent = productLengthCm + ' cm';
    if (this.note) {
      const hemY = yBotEff;
      if (hemY < yWaist + 15) this.note.textContent = 'Bu ceket sende crop / kısa boylu durur.';
      else if (hemY <= yHip + 20) this.note.textContent = 'Bu ceket sende kalça hizasında biter.';
      else this.note.textContent = 'Bu ceket sende uzun blazer (kalça altı) olarak durur.';
    }
  }

  /* ===== Beden hesabı ===== */

  findSize(value, field) {
    const tol = (field.tolerance_cm || 0) + (this.isStretchy ? (field.extra_tolerance_when_stretchy || 0) : 0);
    for (const row of this.chart.rows) {
      if (row[field.key] + tol >= value) return row.beden;
    }
    return this.chart.rows[this.chart.rows.length - 1].beden;
  }
  bedenIndex(beden) {
    return this.chart.rows.findIndex((r) => r.beden === beden);
  }

  calculate() {
    const inputs = {};
    for (const f of this.primaryFields) {
      const el = this.form.querySelector(`input[name="${f.key}"]`);
      const v = parseInt(el?.value, 10);
      if (!v) { alert(f.label + ' alanını doldur.'); return; }
      inputs[f.key] = v;
    }
    const recPerField = this.primaryFields.map((f) => ({ field: f, beden: this.findSize(inputs[f.key], f) }));
    const sorted = [...recPerField].sort((a, b) => this.bedenIndex(b.beden) - this.bedenIndex(a.beden));
    const final = sorted[0].beden;

    this.querySelectorAll('[data-sg-row]').forEach((tr) => {
      tr.classList.toggle('sg__chart-row--rec', tr.dataset.beden === final);
    });

    this.recSize.textContent = final;
    const distinct = [...new Set(recPerField.map((r) => r.beden))];
    let note;
    if (distinct.length === 1) {
      note = `Tüm ölçülerin ${final} bedene uygun.`;
    } else {
      const parts = recPerField.map((r) => `${r.field.label.toLowerCase()} ${r.beden}`).join(', ');
      note = `Ölçülerin: ${parts} bedene denk geliyor — rahat kullanım için ${final} öneriyoruz.`;
    }
    if (this.isStretchy) note += ' Bu üründe esnek kumaş var; basende ekstra tolerans uygulandı.';
    this.recNote.textContent = note;
    this.result.hidden = false;
    this.result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    this.updateAvatar();
  }
}

customElements.define('size-guide-modal', SizeGuideModal);
